import { useEffect, useRef, useCallback } from "react";
import { useEvento } from "@/store/evento/evento";
import { useWizardContext, useEventoContext } from "../contexts";
import { trackCompraSSE } from "@/lib/services/eventos";
import { useComprarButton } from "./useComprarButton";
import { WIZARD_STEPS, COMPRA_STATUS, COMPRA_STATUS_CONFIG, COMPRA_MESSAGES } from "../constants";
import { useClearOccupiedSeats } from "./useOccupiedSeats";
import { trackPurchase } from "@/lib/utils/metaPixel";
import { connection } from "@/lib/connection";
import { apiUrl } from "@/lib/config";

/*
   La cookie `compra_access_token` la setea handlePaymentSuccess con max-age 5min
   para autenticar el SSE. La borramos defensivamente al cerrar; el max-age
   también la mata sola si el JS no llega a limpiar.
*/
const clearTrackCookie = () => {
    document.cookie = 'compra_access_token=; max-age=0; path=/';
};

/**
 * Cada cuánto se sondea el endpoint /status mientras el SSE está activo.
 * El primero en responder con "confirmed" cancela el resto.
 */
const POLL_INTERVAL_MS = 2000; // 2 segundos

/**
 * Tiempo máximo total de espera (SSE + polling) antes de rendirse.
 * Cubre el peor caso donde el worker de BullMQ tarda más de lo esperado.
 */
const GLOBAL_TIMEOUT_MS = 90 * 1000; // 90 segundos

type CompraStatusPayload = {
    compraId: number;
    status: string;
    confirmed: boolean;
    pdfUrl: string | null;
    tickets: any[];
};

/**
 * Consulta el estado actual de la compra via HTTP.
 * Retorna null si hay un error de red o la respuesta no es parseable.
 */
async function fetchCompraStatus(compraId: string): Promise<CompraStatusPayload | null> {
    try {
        const url = `${apiUrl('/compras')}/${compraId}/status`;
        const raw = await connection<any>(url, 'GET');
        // ResponseFactory envuelve en { data: {...}, success, msg }
        return raw?.data ?? raw ?? null;
    } catch {
        return null;
    }
}

export const useTrackCompra = () => {
    const sseRef = useRef<EventSource | null>(null);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const globalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Bandera para evitar doble-resolución si SSE y poll llegan casi al mismo tiempo
    const resolvedRef = useRef(false);

    const { moveTo } = useWizardContext();
    const { mapa } = useEventoContext();

    const { state: compraStatus, set: setCompraStatus } = useEvento.use.compra();
    const { set: setIsLoading } = useEvento.use.isLoading();
    const { state: entradas, set: setEntradas } = useEvento.use.entradas();

    const { total } = useComprarButton(mapa);

    const clearOccupiedSeats = useClearOccupiedSeats();

    /**
     * Limpia toda la infraestructura de tracking (SSE + polling + timeout global).
     */
    const cleanup = useCallback(() => {
        if (globalTimeoutRef.current) {
            clearTimeout(globalTimeoutRef.current);
            globalTimeoutRef.current = null;
        }

        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }

        if (sseRef.current) {
            console.log('[TRACK] Cerrando conexión SSE');
            sseRef.current.close();
            sseRef.current = null;
            clearTrackCookie();
        }
    }, []);

    // Expuesto para cierre externo (navegación, panel close)
    const closeSSE = useCallback(() => cleanup(), [cleanup]);

    /**
     * Aplica el estado de éxito al store y navega al paso final.
     * Idempotente: si ya se resolvió (doble señal SSE+poll), no hace nada.
     */
    const applySuccess = useCallback((data: {
        compraId: number | string;
        tickets?: any[];
        pdfUrl?: string | null;
    }) => {
        if (resolvedRef.current) return;
        resolvedRef.current = true;

        trackPurchase(total, 'ARS', String(data.compraId));

        const formatId = (id: string | undefined | null) => {
            if (!id) return id;
            const parts = id.split('-');
            return parts[parts.length - 1];
        };

        const entradasDisponibles = JSON.parse(JSON.stringify(entradas));
        const enrichedTickets = (data.tickets || []).map((t: any) => {
            const entrada = entradasDisponibles.find((e: any) => e.tipoEntradaId === t.tipoEntradaId);
            if (entrada && entrada.asientos && entrada.asientos.length > 0) {
                const asiento = entrada.asientos.pop();
                return { ...t, mesa: formatId(asiento.mesaId), asiento: formatId(asiento.seatId) };
            }
            return t;
        });

        setCompraStatus({
            ...compraStatus,
            status: COMPRA_STATUS.CONFIRMADA,
            message: COMPRA_STATUS_CONFIG[COMPRA_STATUS.CONFIRMADA].message,
            compraId: String(data.compraId),
            tickets: enrichedTickets,
            pdfUrl: data.pdfUrl || null,
        });

        setIsLoading(false);
        moveTo(WIZARD_STEPS.COMPLETADO);
        setEntradas([]);
        clearOccupiedSeats();
        cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [total, entradas, compraStatus, setCompraStatus, setIsLoading, moveTo, setEntradas, clearOccupiedSeats, cleanup]);

    /**
     * Aplica estado de timeout/rechazo.
     * Idempotente: no hace nada si ya hubo una resolución exitosa.
     */
    const applyTimeout = useCallback(() => {
        if (resolvedRef.current) return;
        resolvedRef.current = true;

        console.warn('[TRACK] Timeout global alcanzado — mostrando error de timeout');
        cleanup();
        setCompraStatus({
            status: COMPRA_STATUS.RECHAZADA,
            message: COMPRA_MESSAGES.TIMEOUT,
            compraId: null,
        });
        setIsLoading(false);
        moveTo(WIZARD_STEPS.COMPLETADO);
    }, [cleanup, setCompraStatus, setIsLoading, moveTo]);

    /**
     * Maneja el evento SSE 'compra'. Puede llegar en cualquier momento.
     */
    const handleCompra = useCallback((event: MessageEvent) => {
        const data = JSON.parse(event.data);

        if (data.status === COMPRA_STATUS.PENDIENTE) return;

        if (data.status === COMPRA_STATUS.CONFIRMADA) {
            console.log('[SSE] Evento de confirmación recibido');
            applySuccess({
                compraId: data.compraId,
                tickets: data.tickets,
                pdfUrl: data.pdfUrl,
            });
            return;
        }

        if (data.status === COMPRA_STATUS.RECHAZADA) {
            if (resolvedRef.current) return;
            resolvedRef.current = true;

            cleanup();
            setCompraStatus({
                status: COMPRA_STATUS.RECHAZADA,
                message: COMPRA_STATUS_CONFIG[COMPRA_STATUS.RECHAZADA].message,
                compraId: null,
            });
            setIsLoading(false);
            moveTo(WIZARD_STEPS.COMPLETADO);
            setEntradas([]);
            clearOccupiedSeats();
        }
    }, [applySuccess, cleanup, setCompraStatus, setIsLoading, moveTo, setEntradas, clearOccupiedSeats]);

    /**
     * Error del EventSource — los browsers reconectan solos ante fallos transitorios.
     * NO mostramos error: el polling HTTP y el timeout global actúan como red de seguridad.
     */
    const handleSSEError = (e: Event) => {
        console.warn('[SSE] Error de conexión — el navegador reintentará automáticamente', e);
    };

    useEffect(() => {
        // No iniciar tracking si no estamos en proceso o falta el compraId
        if (compraStatus.status !== COMPRA_STATUS.EN_PROCESO || !compraStatus.compraId || total <= 0) {
            return;
        }

        // Evitar doble arranque si el effect se re-ejecuta
        if (sseRef.current || pollIntervalRef.current) return;

        resolvedRef.current = false;
        const compraId = compraStatus.compraId;

        // ─────────────────────────────────────────────────────────────────────
        // PASO 1: Fetch HTTP inmediato
        // Si la compra ya está confirmada (ej: webhook llegó muy rápido), 
        // mostramos el éxito al instante sin abrir SSE ni esperar.
        // ─────────────────────────────────────────────────────────────────────
        fetchCompraStatus(compraId).then((statusData) => {
            if (resolvedRef.current) return; // ya se resolvió por otra vía

            if (statusData?.confirmed) {
                console.log('[TRACK] Compra ya confirmada en primer fetch — resolución inmediata');
                applySuccess({
                    compraId: statusData.compraId,
                    tickets: statusData.tickets,
                    pdfUrl: statusData.pdfUrl,
                });
                return;
            }

            // ─────────────────────────────────────────────────────────────────
            // PASO 2: La compra sigue procesando → abrir SSE + polling cada 2s
            // El que llegue primero gana; ambos son idempotentes.
            // ─────────────────────────────────────────────────────────────────
            console.log(`[TRACK] Compra ${compraId} aún en proceso — activando SSE + polling`);

            // SSE
            const sse = trackCompraSSE(compraId);
            sseRef.current = sse;
            sse.addEventListener('compra', handleCompra);
            sse.addEventListener('error', handleSSEError);

            // Polling cada POLL_INTERVAL_MS
            pollIntervalRef.current = setInterval(async () => {
                if (resolvedRef.current) {
                    clearInterval(pollIntervalRef.current!);
                    pollIntervalRef.current = null;
                    return;
                }

                const poll = await fetchCompraStatus(compraId);
                if (!poll) return; // error de red, reintentará en el próximo tick

                if (poll.confirmed) {
                    console.log('[POLL] Compra confirmada en polling HTTP');
                    clearInterval(pollIntervalRef.current!);
                    pollIntervalRef.current = null;
                    applySuccess({
                        compraId: poll.compraId,
                        tickets: poll.tickets,
                        pdfUrl: poll.pdfUrl,
                    });
                }
            }, POLL_INTERVAL_MS);

            // Timeout global: si después de GLOBAL_TIMEOUT_MS ninguno resolvió, rendirse
            globalTimeoutRef.current = setTimeout(applyTimeout, GLOBAL_TIMEOUT_MS);
        });

        return () => {
            cleanup();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [compraStatus.status, compraStatus.compraId, total]);

    return { closeSSE };
};
